import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentLedger } from './api/studentScreensApi'

const PAGE_SIZES = [10, 25, 50, 100]

function KPICard({ label, value, color }) {
  const colorMap = { blue: 'bg-blue-500 text-white', green: 'bg-green-500 text-white', purple: 'bg-purple-500 text-white', orange: 'bg-orange-500 text-white' }
  return (
    <div className={`flex flex-col items-center justify-center rounded-2xl px-4 py-5 ${colorMap[color]}`}>
      <span className="text-xl font-black">{value}</span>
      <span className="mt-1.5 text-[11px] font-bold uppercase tracking-wider opacity-90">{label}</span>
    </div>
  )
}

function FilterSelect({ label, options }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">{label}</span>
      <select className="rounded-xl border-0 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  )
}

function FilterInput({ label, type = 'text', placeholder }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">{label}</span>
      <input type={type} placeholder={placeholder} className="rounded-xl border-0 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/20" />
    </label>
  )
}

function fmt(n) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2 })
}

export default function StudentLedgerScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pageSize, setPageSize] = useState(25)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [entries, setEntries] = useState([])
  const [totals, setTotals] = useState({ opening: 0, debit: 0, credit: 0, balance: 0, balanceSide: 'Dr' })

  useEffect(() => { loadStudent() }, [studentId])
  useEffect(() => { loadLedger() }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadLedger = async () => {
    try {
      const data = await getStudentLedger(studentId)
      setEntries(data?.rows || [])
      setTotals({
        opening: data?.openingBalance || 0,
        debit: data?.totalDebit || 0,
        credit: data?.totalCredit || 0,
        balance: data?.balance || 0,
        balanceSide: data?.balanceSide || 'Dr',
      })
    } catch {
      setEntries([])
      setTotals({ opening: 0, debit: 0, credit: 0, balance: 0, balanceSide: 'Dr' })
    }
  }

  const pagedEntries = useMemo(() => entries.slice(0, pageSize), [entries, pageSize])

  const goBack = () => navigate('/admission/students/list')
  const name = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'
  const sid = student?.studentCode || student?.id || '—'

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Ledger</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">Ledger — {name}#{sid}</h1>
        </div>
        <button onClick={goBack} className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80">
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back
        </button>
      </section>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KPICard label="Opening Balance" value={`₹${fmt(totals.opening)}`} color="blue" />
        <KPICard label="Debits" value={`₹${fmt(totals.debit)}`} color="green" />
        <KPICard label="Credits" value={`₹${fmt(totals.credit)}`} color="purple" />
        <KPICard label={`${totals.balanceSide || 'Dr'} Balance`} value={`₹${fmt(totals.balance)}`} color="orange" />
      </div>

      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-on-primary text-[22px]">table_chart</span>
          <h2 className="text-base font-bold text-on-primary">Ledger for {name}#{sid}</h2>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} className="inline-flex items-center gap-1.5 rounded-xl bg-white/15 px-3 py-1.5 text-xs font-bold text-on-primary transition-colors hover:bg-white/25">
            <span className="material-symbols-outlined text-[16px]">filter_list</span>
            Filter
          </button>
          <span className="text-xs font-bold text-on-primary/80">Show</span>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-lg border border-white/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-white/40">
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs font-bold text-on-primary/80">entries</span>
        </div>
      </div>

      {filtersOpen && (
      <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-5 transition-all duration-200">
        <div className="flex flex-wrap items-end gap-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <FilterSelect label="Company" options={['All']} />
            <FilterSelect label="Branch" options={['All']} />
            <FilterSelect label="Department" options={['All']} />
            <FilterSelect label="Place" options={['All']} />
            <FilterInput label="Ref No." placeholder="Search ref..." />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <FilterSelect label="Type" options={['All', 'Sale', 'Payment', 'Receipt']} />
            <FilterInput label="Period From" type="date" />
            <FilterInput label="Period To" type="date" />
            <FilterInput label="Amount From" type="number" placeholder="Min" />
            <FilterInput label="Amount To" type="number" placeholder="Max" />
          </div>
          <div className="flex items-end gap-3">
            <FilterSelect label="Unapproved" options={['No', 'Yes']} />
            <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90">
              <span className="material-symbols-outlined text-[16px]">search</span>
              Search
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-surface-variant px-4 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-variant/80">
              <span className="material-symbols-outlined text-[16px]">filter_list_off</span>
              Show All
            </button>
          </div>
        </div>
      </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/30 bg-surface-container-low">
              {['Action', 'Date', 'No.', 'Ref.', 'Type', 'A/c Head'].map((h) => (
                <th key={h} className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">{h}</th>
              ))}
              {['Debit', 'Credit', 'Balance'].map((h) => (
                <th key={h} className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant text-right">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                  <p className="mt-3 text-sm text-on-surface-variant">Loading ledger...</p>
                </td>
              </tr>
            ) : pagedEntries.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-12">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                      No entries meet selected criteria — Either there are no entries in the selected module or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {pagedEntries.map((entry, idx) => (
                  <tr key={entry.id} className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:bg-error/10 hover:text-error transition-colors" title="Delete">
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                        </button>
                        <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-on-surface-variant hover:bg-primary/10 hover:text-primary transition-colors" title="Print">
                          <span className="material-symbols-outlined text-[16px]">print</span>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-on-surface font-mono">{entry.date}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-on-surface">{entry.no}</td>
                    <td className="px-4 py-3 text-sm text-on-surface-variant font-mono">{entry.ref}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-[10px] font-bold text-blue-700">{entry.type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-on-surface">{entry.acHead}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface font-mono">{entry.debit > 0 ? fmt(entry.debit) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface font-mono">{entry.credit > 0 ? fmt(entry.credit) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-on-surface font-mono">{fmt(entry.balance)} {entry.balanceSide}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-outline-variant/30 bg-surface-container-low font-bold">
                  <td colSpan={6} className="px-4 py-3.5 text-sm text-on-surface">Closing Balance</td>
                  <td className="px-4 py-3.5 text-sm text-right font-mono text-on-surface">{totals.debit > 0 ? fmt(totals.debit) : '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-mono text-on-surface">{totals.credit > 0 ? fmt(totals.credit) : '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-black text-primary font-mono">{fmt(totals.balance)} {totals.balanceSide || 'Dr'}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on <span className="font-semibold text-on-surface">{student.createdDate}</span></span>
          )}
          {student?.updatedBy && (
            <span>Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on <span className="font-semibold text-on-surface">{student.updatedDate}</span></span>
          )}
        </div>
      </section>
    </div>
  )
}
